import React, {useState} from 'react';
import {Modal, TextInput, TouchableOpacity, View} from 'react-native';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import loginTestIDs from '/constant/TestIDs/Login';
import {
  EVENT_NAMES,
  FORGOT_PASSWORD_ERROR_STATE,
  LOGIN_ENABLE,
  LOGIN_STATUS,
} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {gallery, salmonPink} from '/theme/color/secondary.color';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';
import {validateEmail} from '/util/validation.util';

import {styles} from './ForgotPasswordModal.style';
interface ForgotPasswordModalProps {
  isModalVisible: boolean;
  onResetPassword: (email: string) => Promise<void>;
  setModalVisible: (visible: boolean) => void;
  setConfirmationMessage: (message: string) => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isModalVisible,
  setModalVisible,
  onResetPassword,
  setConfirmationMessage,
}) => {
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const {ts} = useTranslation('ForgotPasswordModal');

  const errorMessage = ts('emailError', 'Please enter a valid email address');
  const confirmationMessage = ts('confirmMessage', 'Reset email sent');

  const sendForgotPasswordSubmitEvent =
    getTealiumUtilDefaultInstance().generateTrackEvent({
      eventName: EVENT_NAMES.forgetPasswordSubmit,
    });
  const handleResetPassword = async () => {
    if (!validateEmail(email)) {
      setEmailError(errorMessage);
      sendForgotPasswordSubmitEvent({
        eventName: EVENT_NAMES.forgetPasswordSubmit,
        error_state: FORGOT_PASSWORD_ERROR_STATE.true,
        login_enable: LOGIN_ENABLE.false,
        success: LOGIN_STATUS.fail,
        email: email,
      });
      return;
    }

    try {
      await onResetPassword(email);
      setConfirmationMessage(confirmationMessage);
      setModalVisible(false);
      setEmail('');
      sendForgotPasswordSubmitEvent({
        eventName: EVENT_NAMES.forgetPasswordSubmit,
        error_state: FORGOT_PASSWORD_ERROR_STATE.false,
        login_enable: LOGIN_ENABLE.false,
        success: LOGIN_STATUS.success,
        email: email,
      });
    } catch (error) {
      setModalVisible(false);
      setEmailError('');
      setEmail('');
    }
  };
  const handleCloseModal = () => {
    setModalVisible(false);
    setEmailError('');
    setEmail('');
  };
  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (text === '') {
      setEmailError('');
      return;
    }
    if (!validateEmail(text)) {
      setEmailError(errorMessage);
    } else {
      setEmailError('');
    }
  };

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={isModalVisible}
      onRequestClose={handleCloseModal}>
      <View style={styles.modalView}>
        <View style={styles.modalContent}>
          <View style={styles.topView}>
            <Text
              style={styles.modalText}
              isBold
              testID="forgot-password-modal-header"
              {...testProperties(loginTestIDs.forgotPasswordModalHeader)}>
              {ts('forgot password', 'Forgot password?')}
            </Text>
            <TouchableOpacity
              onPress={handleCloseModal}
              accessibilityRole="button"
              accessibilityLabel="Close forgot password modal"
              testID={loginTestIDs.forgotPasswordModalCloseButton}
              style={styles.closeButton}>
              <Icon name="close" style={styles.crossIcon} />
            </TouchableOpacity>
          </View>
          <Text
            style={styles.labelText}
            {...testProperties(loginTestIDs.forgotPasswordModalDescription)}>
            {ts(
              'reset message',
              'We’ll send you an email to reset your \n password.',
            )}
          </Text>
          <Text
            style={styles.emailLabel}
            isBold
            {...testProperties(loginTestIDs.forgotPasswordModalEmailLabel)}>
            {ts('email', 'Email')}
          </Text>
          <TextInput
            style={[
              styles.input,
              {borderColor: emailError ? salmonPink : gallery},
            ]}
            placeholder="Enter your email"
            accessibilityLabel="Enter your email address"
            value={email}
            onChangeText={handleEmailChange}
            testID={loginTestIDs.forgotPasswordModalEmailInput}
            {...testProperties(loginTestIDs.forgotPasswordModalEmailInput)}
          />
          {emailError ? (
            <View style={styles.alertView}>
              <Icon name="alert" style={styles.alertIcon} />
              <Text
                style={styles.errorText}
                {...testProperties(loginTestIDs.forgotPasswordModalError)}>
                {emailError}
              </Text>
            </View>
          ) : null}
          <TouchableOpacity
            accessibilityRole="button"
            testID="reset-password-button"
            accessibilityLabel="Reset Password"
            onPress={handleResetPassword}
            style={styles.resetButton}>
            <Text
              style={styles.resetButtonText}
              isBold
              {...testProperties(loginTestIDs.forgotPasswordModalResetBtn)}>
              {ts('reset button', 'Reset password')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default ForgotPasswordModal;
